'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Button, IconButton } from '../../atoms/Button';
import { RichText } from '../RichText';
import { FormModalProps } from './FormModal.types';

const modalVariants = cva('', {
  variants: {
    size: {
      xsmall: [
        'w-full h-[50%] max-h-[406px]',
        'md:w-6/12 h-[50%] md:max-h-[540px]',
        'lg:w-4/12 h-[50%] lg:max-h-[540px]',
      ].join(' '),
      small: [
        'w-full h-[70%] max-h-[568px]',
        'md:w-8/12 h-[70%] md:max-h-[756px]',
        'lg:w-6/12 h-[70%] lg:max-h-[756px]',
      ].join(' '),
      medium: [
        'w-full h-[84%] max-h-[682px]',
        'md:w-10/12 h-[84%] md:max-h-[908px]',
        'lg:w-8/12 h-[84%] lg:max-h-[908px]',
      ].join(' '),
      large: [
        'w-full h-full',
        'md:w-[calc(100%-64px)] md:max-h-[96%]',
        'lg:w-10/12 lg:max-h-[96%]',
      ].join(' '),
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

// Centered modal styles (default)
const contentCenteredClasses =
  'z-modal fixed overflow-hidden ' +
  'shadow-card outline-none ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover ' +
  'left-0 right-0 bottom-0 rounded-t-lg ' +
  'md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:rounded-lg';

// Fullscreen modal styles
const contentFullscreenClasses =
  'z-modal fixed inset-0 overflow-y-auto outline-none ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover';

const contentLightClasses = 'bg-white';
const contentDarkClasses = 'bg-grey-900';
const contentFullscreenDarkClasses = 'bg-black';

const contentCenteredTransitionClasses =
  'transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] ' +
  'data-[state=closed]:translate-y-full data-[state=open]:translate-y-0 ' +
  'data-[state=closed]:opacity-0 data-[state=open]:opacity-100 ' +
  'md:data-[state=closed]:scale-95 md:data-[state=open]:scale-100 ' +
  'md:data-[state=closed]:-translate-y-1/2 md:data-[state=open]:-translate-y-1/2';

const contentFullscreenTransitionClasses =
  'transition-opacity duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] ' +
  'data-[state=closed]:opacity-0 data-[state=open]:opacity-100';

export const FormModal = (props: FormModalProps) => {
  const {
    cancel,
    className = '',
    close,
    content,
    customFooter,
    dark = false,
    description,
    descriptionClassName = '',
    headerClassName = '',
    footerClassName = '',
    disabled = false,
    isSubmitting = false,
    headerVariant = 'default',
    onCancel,
    onOpenChange,
    onSubmit,
    open: controlledOpen,
    size = 'medium',
    submit,
    subtitle,
    support,
    title,
    trigger,
    variant = 'centered',
    isStickyFooter = true,
  } = props;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange || (() => {}) : setInternalOpen;
  const isFullscreen = variant === 'fullscreen';
  const isMinimalHeader = headerVariant === 'minimal';

  const handleSubmit = () => {
    onSubmit?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const renderMinimalHeader = () => (
    <div
      className={c('flex items-center justify-end px-4 py-8', headerClassName)}
    />
  );

  const renderDefaultHeader = () => (
    <div
      className={c(
        'sticky top-0 z-10 flex shrink-0 flex-col md:pt-4',
        dark ? 'bg-grey-900' : 'bg-white',
        headerClassName
      )}
    >
      <div className="mt-4 flex items-start justify-between px-5 pb-2 md:mt-0">
        <div className="sm:mr-[20%] mr-12 flex w-full flex-col gap-1">
          <Dialog.Title
            className={c(
              'text-xl font-semibold md:text-2xl md:leading-8',
              dark ? 'text-grey-100' : 'text-grey-900'
            )}
          >
            {title}
          </Dialog.Title>
          {subtitle ? (
            <Dialog.Description
              className={c('text-sm', dark ? 'text-grey-400' : 'text-grey-700')}
            >
              {subtitle}
            </Dialog.Description>
          ) : null}
        </div>
        {/* Spacer for the close button that's positioned absolutely */}
      </div>
      <div
        className={c(
          'w-full border-b',
          dark ? 'border-grey-800' : 'border-grey-200'
        )}
      />
    </div>
  );

  const renderHeader = () =>
    isMinimalHeader ? renderMinimalHeader() : renderDefaultHeader();

  const renderMinimalHeaderCloseButton = () => (
    <Dialog.Close asChild>
      <IconButton
        iconName="X"
        ariaLabel={close}
        iconSize={20}
        iconWeight="regular"
        iconClassName={c(
          isFullscreen && dark ? 'fill-grey-400' : 'fill-grey-700'
        )}
        onClick={handleCancel}
        className="absolute right-3.5 top-4 z-20 w-auto px-1.5 py-1.5"
      />
    </Dialog.Close>
  );

  const renderCloseButton = () => (
    <Dialog.Close asChild>
      <Button.v2
        iconLeft="X"
        size="xsmall"
        aria-label={close}
        iconSize={20}
        iconWeight="bold"
        shape="square"
        variant={dark ? 'dark-tertiary' : 'tertiary-grey-monochrome'}
        onClick={handleCancel}
        className="absolute right-5 top-4 z-20 md:top-4"
      />
    </Dialog.Close>
  );

  const renderFooterButtons = () => (
    <div className="flex flex-col gap-3 md:flex-col md:gap-3 lg:flex-row lg:items-center lg:justify-end lg:gap-4">
      {submit ? (
        <Button.v2
          label={submit}
          disabled={disabled}
          className={c(
            'order-1 w-full lg:order-2',
            dark &&
              'disabled:border-grey-800 disabled:bg-grey-800 disabled:text-grey-600',
            !cancel ? 'lg:w-full' : 'lg:w-auto'
          )}
          variant="primary"
          size={size === 'xsmall' ? 'medium' : 'large'}
          shape="square"
          onClick={handleSubmit}
          iconLeft={isSubmitting ? 'SpinnerGap' : undefined}
          iconWeight={isSubmitting ? 'bold' : undefined}
          iconClassName={isSubmitting ? 'animate-spin' : undefined}
          iconSize={isSubmitting ? 20 : undefined}
          data-testid="form-modal-submit-button"
          type="button"
        />
      ) : null}
      {cancel ? (
        <Dialog.Close asChild>
          <Button.v2
            label={cancel}
            className="order-2 w-full lg:order-1 lg:w-auto"
            variant="tertiary-black-monochrome"
            size={size === 'xsmall' ? 'medium' : 'large'}
            shape="square"
            onClick={handleCancel}
          />
        </Dialog.Close>
      ) : null}
    </div>
  );

  const renderSupportDisclaimer = () => {
    if (!support) {
      return null;
    }

    return (
      <div
        className={c(
          'mt-4 border-t pt-2',
          dark ? 'border-grey-800' : 'border-grey-200'
        )}
      >
        <div className="sm:mr-[20%] w-full space-y-1">
          <p
            className={c(
              'text-sm font-medium leading-5 tracking-tight',
              dark ? 'text-grey-100' : 'text-grey-900'
            )}
          >
            {support.title}
          </p>
          <RichText
            html={support.description}
            className={c(
              'text-sm font-normal leading-5 tracking-tight [&_a]:text-sm [&_p]:mb-0',
              dark ? 'text-grey-400' : 'text-grey-600'
            )}
          />
        </div>
      </div>
    );
  };

  const renderFooter = () => (
    <div
      className={c(
        `${isStickyFooter ? 'sticky' : ''} bottom-0 z-10 mt-auto flex shrink-0 flex-col`,
        dark ? 'bg-grey-900' : 'bg-white',
        footerClassName
      )}
    >
      <div
        className={c(
          'w-full border-t',
          dark ? 'border-grey-800' : 'border-grey-200'
        )}
      />
      <div className="px-5 py-4">
        {renderFooterButtons()}
        {renderSupportDisclaimer()}
      </div>
    </div>
  );

  const getContentClasses = () => {
    if (isFullscreen) {
      return c(
        contentFullscreenClasses,
        dark ? contentFullscreenDarkClasses : contentLightClasses,
        contentFullscreenTransitionClasses,
        className
      );
    }

    return c(
      contentCenteredClasses,
      dark ? contentDarkClasses : contentLightClasses,
      contentCenteredTransitionClasses,
      modalVariants({ size }),
      className
    );
  };

  const renderContent = () => {
    if (isFullscreen) {
      return (
        <div className="flex h-full flex-col">
          {renderHeader()}
          <div className="flex-1 overflow-y-auto">{content}</div>
          {customFooter}
        </div>
      );
    }

    return (
      <div className="relative h-full">
        <div className="flex h-full flex-col overflow-y-auto">
          {renderHeader()}
          <div className="flex flex-1 flex-col gap-2 px-5 md:gap-3">
            {description ? (
              <RichText
                html={description}
                className={c(
                  'mt-4 text-base font-normal [&_p]:mb-3',
                  dark ? 'text-grey-100' : 'text-grey-900',
                  descriptionClassName
                )}
              />
            ) : null}
            {content}
          </div>
          {customFooter ?? (submit && renderFooter())}
        </div>
        {!isMinimalHeader && renderCloseButton()}
      </div>
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/50 transition-opacity duration-300"
          style={{ display: open ? 'block' : 'none' }}
        />
        <Dialog.Content className={getContentClasses()}>
          {renderContent()}
          {isMinimalHeader && renderMinimalHeaderCloseButton()}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
